import React, { createContext, useContext, useReducer, useEffect, ReactNode, useState } from 'react';
import { UserResponse, LoginRequest, LoginResponse } from '@troia-v3/types';
import { AuthService } from '@/modules/auth/services';
import { decodeJwtClaims } from '@/lib/jwt';
import { toast } from 'sonner';
import { SocketProvider } from './SocketContext';
import { GlobalSocketListeners } from '@/shared/components/GlobalSocketListeners';
import { TeamChatRoot } from '@/modules/team-chat/components/TeamChatRoot';
import { SavedSession } from '@/types/session';
import {
  loadSavedSessions,
  saveSession,
  removeSession as removeSessionHelper,
  updateLastLogin,
} from '@/lib/sessionHelpers';
import { clearUserFilters } from '@/lib/clearUserFilters';
import { useQueryClient } from '@tanstack/react-query';
import { useOneSignal, oneSignalLogout } from '@/shared/hooks/useOneSignal';
import { chatDraftStore } from '@/modules/chat/services/chatDraftStore';

interface AuthState {
  user: UserResponse | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  masterLogin: boolean;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: LoginResponse }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: UserResponse };

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  masterLogin: false,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        refreshToken: action.payload.refreshToken || null,
        isAuthenticated: true,
        isLoading: false,
        // `masterLogin` vem explicitamente do LoginResponse (fluxo de login fresh)
        // OU do decode do JWT (fluxo de reload com token já armazenado — ver checkAuthStatus).
        masterLogin: action.payload.masterLogin === true,
      };
    case 'LOGOUT':
      return {
        ...initialState,
        isLoading: false,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

interface AuthContextType {
  state: AuthState;
  login: (credentials: LoginRequest) => Promise<LoginResponse>;
  logout: () => void;
  updateUser: (user: UserResponse) => void;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  refreshAuth: () => Promise<void>;
  // Multi-session support
  savedSessions: SavedSession[];
  loginWithSavedSession: (session: SavedSession) => Promise<void>;
  removeSession: (sessionId: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const [savedSessions, setSavedSessions] = useState<SavedSession[]>([]);
  const queryClient = useQueryClient();

  // Initialize OneSignal Web Push when authenticated
  useOneSignal({ userId: state.user?.id, isAuthenticated: state.isAuthenticated });

  // Load saved sessions on mount
  useEffect(() => {
    const sessions = loadSavedSessions();
    setSavedSessions(sessions);
  }, []);

  // Check for existing token and validate on mount
  useEffect(() => {
    const abortController = new AbortController();

    const checkAuthStatus = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
        const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');


        if (!token) {
          if (!abortController.signal.aborted) {
            dispatch({ type: 'SET_LOADING', payload: false });
          }
          return;
        }

        try {
          // Try to get current user to validate token
          const user = await AuthService.getCurrentUser(abortController.signal);

          if (abortController.signal.aborted) return;

          if (!user) {
            throw new Error('No user data returned');
          }

          // Token is valid, update state

          // Handle permissions safely
          let allPermissions: string[] = [];
          try {
            const systemPerms = Array.isArray(user.permissions.system) ? user.permissions.system : [];
            const companyPerms = Array.isArray(user.permissions.company) ? user.permissions.company : [];
            const customPerms = user.permissions.custom ? Object.values(user.permissions.custom).flat() : [];

            allPermissions = [
              ...systemPerms,
              ...companyPerms,
              ...customPerms,
            ];

          } catch (permError) {
            allPermissions = [];
          }

          // Use allPermissions to avoid unused variable warning
          console.debug('User permissions loaded:', allPermissions.length);

          // Extrai claim `masterLogin` do token armazenado (sobrevive reload)
          const claims = decodeJwtClaims(token);
          const isMaster = claims?.masterLogin === true;

          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user,
              token,
              refreshToken: refreshToken || undefined,
              expiresAt: new Date(), // Will be refreshed if needed
              companyName: '', // Will be loaded from saved session
              ...(isMaster ? { masterLogin: true } : {}),
            },
          });
        } catch (error) {
          if (abortController.signal.aborted) return;

          // Token invalid, try refresh if available
          if (refreshToken) {
            try {
              const response = await AuthService.refreshToken(refreshToken, abortController.signal);

              if (abortController.signal.aborted) return;

              // Save new tokens
              const storage = localStorage.getItem('refresh_token') ? localStorage : sessionStorage;
              storage.setItem('auth_token', response.token);
              if (response.refreshToken) {
                storage.setItem('refresh_token', response.refreshToken);
              }

              dispatch({ type: 'LOGIN_SUCCESS', payload: response });
            } catch (refreshError) {
              if (!abortController.signal.aborted) {
                // Refresh failed, logout
                AuthService.logout();
                dispatch({ type: 'LOGOUT' });
              }
            }
          } else {
            if (!abortController.signal.aborted) {
              // No refresh token, logout
              AuthService.logout();
              dispatch({ type: 'LOGOUT' });
            }
          }
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('Auth check error:', error);
          AuthService.logout();
          dispatch({ type: 'LOGOUT' });
        }
      }
    };

    checkAuthStatus();

    // ✅ Cleanup function - abort all requests when component unmounts
    return () => {
      abortController.abort();
    };
  }, []);

  const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Limpar cache do React Query para evitar mostrar dados do usuário anterior
      queryClient.clear();

      // Limpar filtros e preferências do usuário anterior
      clearUserFilters();
      // Rascunhos de chat são por usuário — nunca herdar do login anterior (mesma razão do clearUserFilters)
      chatDraftStore.clearEverything();

      const response = await AuthService.login(credentials);

      // Save tokens based on remember option
      const storage = credentials.rememberMe ? localStorage : sessionStorage;
      storage.setItem('auth_token', response.token);
      if (response.refreshToken) {
        storage.setItem('refresh_token', response.refreshToken);
      }

      dispatch({ type: 'LOGIN_SUCCESS', payload: response });

      // Save session for multi-session support
      const updatedSessions = saveSession(
        {
          id: response.user.id,
          name: `${response.user.firstName} - ${response.companyName}`,
          email: response.user.email,
          avatar: response.user.avatar,
          token: response.token,
          companyId: response.user.companyId,
          companyName: response.companyName,
        },
        credentials.rememberMe || false
      );
      setSavedSessions(updatedSessions);

      return response;
    } catch (error) {
      dispatch({ type: 'SET_LOADING', payload: false });
      throw error;
    }
  };

  const loginWithSavedSession = async (session: SavedSession): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Limpar cache do React Query para evitar mostrar dados do usuário anterior
      queryClient.clear();

      // Limpar filtros e preferências do usuário anterior
      clearUserFilters();
      // Rascunhos de chat são por usuário — nunca herdar do login anterior (mesma razão do clearUserFilters)
      chatDraftStore.clearEverything();

      // Set token in storage
      const storage = session.rememberMe ? localStorage : sessionStorage;
      storage.setItem('auth_token', session.token);

      // Fetch current user to validate token and get fresh data
      const user = await AuthService.getCurrentUser();

      if (!user) {
        throw new Error('Failed to load user data');
      }

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user,
          token: session.token,
          refreshToken: undefined,
          expiresAt: new Date(),
          companyName: session.companyName || '', // From saved session
        },
      });

      // Update last login timestamp
      const updatedSessions = updateLastLogin(session.id);
      setSavedSessions(updatedSessions);

      toast.success(`Bem-vindo de volta, ${session.name}!`);
    } catch (error) {
      // Clear invalid token from storage
      const storage = session.rememberMe ? localStorage : sessionStorage;
      storage.removeItem('auth_token');

      dispatch({ type: 'SET_LOADING', payload: false });

      // Show toast only if not on login page (Login page will handle the error differently)
      if (window.location.pathname !== '/login') {
        toast.error('Não foi possível fazer login com essa conta. Tente novamente.');
      }

      throw error;
    }
  };

  const removeSession = (sessionId: string): void => {
    const updatedSessions = removeSessionHelper(sessionId);
    setSavedSessions(updatedSessions);

    // Show toast only if explicitly deleting (not on error flow in login page)
    if (window.location.pathname !== '/login') {
      toast.success('Conta removida das sessões salvas');
    }
  };

  const logout = () => {
    // Limpar cache do React Query para evitar mostrar dados do usuário após logout
    queryClient.clear();

    // Rascunhos de chat não devem vazar para o próximo usuário (máquina compartilhada).
    // Remove SOMENTE a chave `chat_drafts` — `deviceId` sobrevive ao logout (NUNCA #37).
    chatDraftStore.clearEverything();

    // Logout do OneSignal (desregistrar push notifications)
    oneSignalLogout();

    AuthService.logout();
    dispatch({ type: 'LOGOUT' });
    toast.success('Logout realizado com sucesso!');
  };

  const updateUser = (user: UserResponse) => {
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const refreshAuth = async () => {
    const refreshToken = localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await AuthService.refreshToken(refreshToken);

      // Save new tokens
      const storage = localStorage.getItem('refresh_token') ? localStorage : sessionStorage;
      storage.setItem('auth_token', response.token);
      if (response.refreshToken) {
        storage.setItem('refresh_token', response.refreshToken);
      }

      dispatch({ type: 'LOGIN_SUCCESS', payload: response });
    } catch (error) {
      // Refresh failed, logout
      logout();
      throw error;
    }
  };

  const hasPermission = (permission: string): boolean => {
    if (!state.user?.permissions) return false;

    const { system, company, custom } = state.user.permissions;

    try {
      const hasSystemPerm = Array.isArray(system) && system.includes(permission);
      const hasCompanyPerm = Array.isArray(company) && company.includes(permission);
      const hasCustomPerm = custom && Object.values(custom).some(perms =>
        Array.isArray(perms) && perms.includes(permission)
      );

      return hasSystemPerm || hasCompanyPerm || hasCustomPerm;
    } catch (error) {
      console.error('Error checking permissions:', error);
      return false;
    }
  };

  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  };

  const value: AuthContextType = {
    state,
    login,
    logout,
    updateUser,
    hasPermission,
    hasAnyPermission,
    refreshAuth,
    savedSessions,
    loginWithSavedSession,
    removeSession,
  };

  return (
    <AuthContext.Provider value={value}>
      <SocketProvider>
        <GlobalSocketListeners />
        <TeamChatRoot>
          {children}
        </TeamChatRoot>
      </SocketProvider>
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};